// ========== مشخصات فنی پانل ==========
// اندپوینت‌های سرور (ProformaInvoiceItemOut و ProformaInvoiceProductionItemListItem)
// خودشان فیلدهای فنی را برمی‌گردانند. رمزگشایی کد کالا فقط به‌عنوان جایگزین استفاده
// می‌شود، برای وقتی که سرور آن فیلد را null داده باشد.

const toEnglishDigits = (str: string): string =>
  str
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString())
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString());

export const PANEL_TYPE_MAP: Record<string, string> = {
  "01": "دیواری",
  "02": "سقفی",
  "03": "پیچ مخفی",
  "04": "دیواری بدون درز",
  "05": "دیواری یک طرف بدون درز",
  "06": "سقفی یک طرف بدون درز",
  "07": "در",
};

export const TOP_SHEET_MATERIAL_MAP: Record<string, string> = {
  "1": "گالوانیزه",
  "2": "آلوزینک",
  "3": "گالوالوم",
  "4": "استیل",
  "5": "آلومینیوم",
  "6": "کاغذ-کرافت",
  "7": "فویل آلومینیوم",
  "8": "سلفون",
};

export const BOTTOM_SHEET_MATERIAL_MAP: Record<string, string> = {
  "1": "گالوانیزه",
  "2": "آلوزینک",
  "4": "استیل",
};

export const FOAM_TYPE_MAP: Record<string, string> = {
  "1": "PUR",
  "2": "PIR",
};

export const SHEET_COLOR_MAP: Record<string, string> = {
  "0": "مشکی",
  "1": "زرد",
  "2": "نارنجی",
  "3": "قرمز",
  "4": "کرم",
  "5": "آبی",
  "6": "سبز",
  "7": "طوسی",
  "9": "سفید",
};

export interface DecodedPanelSpec {
  panelType: string;
  thickness: string;
  topSheet: string;
  bottomSheet: string;
  topSheetThickness: string;
  bottomSheetThickness: string;
  foamDensity: string;
  foamType: string;
  topColor: string;
  bottomColor: string;
  description: string;
}

export const decodeGoodsCode = (rawCode?: string | null): DecodedPanelSpec | null => {
  if (!rawCode) return null;
  const code = toEnglishDigits(rawCode).replace(/[^0-9]/g, "");
  if (code.length < 14) return null;

  const panelTypeDigits = code.slice(0, 2);
  const thicknessDigits = code.slice(2, 4);
  const topSheetDigit = code.slice(4, 5);
  const bottomSheetDigit = code.slice(5, 6);
  const topSheetThicknessDigits = code.slice(6, 8);
  const bottomSheetThicknessDigits = code.slice(8, 10);
  const foamDensityDigits = code.slice(10, 12);
  const foamTypeDigit = code.slice(12, 13);
  const topColorDigit = code.slice(13, 14);
  const bottomColorDigit = code.length >= 15 ? code.slice(14, 15) : "";

  const panelType = PANEL_TYPE_MAP[panelTypeDigits] || panelTypeDigits;
  const thickness = String(Number(thicknessDigits));

  return {
    panelType,
    thickness,
    topSheet: TOP_SHEET_MATERIAL_MAP[topSheetDigit] || topSheetDigit,
    bottomSheet: BOTTOM_SHEET_MATERIAL_MAP[bottomSheetDigit] || bottomSheetDigit,
    topSheetThickness: ltrValue(`${(Number(topSheetThicknessDigits) / 100).toFixed(2)} ± 0.02`),
    bottomSheetThickness: ltrValue(`${(Number(bottomSheetThicknessDigits) / 100).toFixed(2)} ± 0.02`),
    foamDensity: foamDensityWithTolerance(Number(foamDensityDigits)),
    foamType: FOAM_TYPE_MAP[foamTypeDigit] || foamTypeDigit,
    topColor: SHEET_COLOR_MAP[topColorDigit] || topColorDigit,
    bottomColor: bottomColorDigit ? SHEET_COLOR_MAP[bottomColorDigit] || bottomColorDigit : "-",
    description: `${panelType} ${thickness} سانت`,
  };
};

/** فیلدهای فنی‌ای که سرور روی هر قلم برمی‌گرداند. */
export interface PanelSpecSource {
  goods_code?: string | null;
  panel_type?: string | null;
  panel_thickness?: number | null;
  top_sheet_material?: string | null;
  bottom_sheet_material?: string | null;
  top_sheet_thickness?: number | null;
  bottom_sheet_thickness?: number | null;
  foam_density?: number | null;
  foam_type?: string | null;
  top_sheet_color?: string | null;
  bottom_sheet_color?: string | null;
}

/**
 * مقدارهایی مثل «0.48 ± 0.02» داخل متن راست‌به‌چپ توسط الگوریتم دوجهته‌ی یونیکد
 * جابه‌جا می‌شوند و «0.02 ± 0.48» دیده می‌شوند، چون هر عدد یک اجرای LTR جداست و
 * ± بینشان خنثی است. با محصورکردن در LRI…PDI جهت داخل رشته قفل می‌شود و مقدار
 * همیشه همان‌طور که نوشته شده دیده می‌شود: عدد اصلی سمت چپ، رواداری سمت راست.
 */
export const ltrValue = (text: string) => `\u2066${text}\u2069`;

/**
 * دانسیته فوم با رواداری ±۲ نمایش داده می‌شود (واحد Kg/M3، مثل «40 ± 2»).
 *
 * رواداری دانسیته با رواداری ضخامت ورق فرق دارد: ضخامت ورق میلی‌متر است و ±۰.۰۲،
 * ولی دانسیته عدد صحیح Kg/M3 است و ±۲؛ ضمناً تقسیم بر ۱۰۰ هم نمی‌شود.
 */
export const foamDensityWithTolerance = (value?: number | string | null): string => {
  if (value === undefined || value === null || value === "") return "-";
  const numeric = Number(value);
  if (isNaN(numeric)) return String(value);
  return ltrValue(`${numeric} ± 2`);
};

// ضخامت ورق‌ها با رواداری نمایش داده می‌شود (همان قالبی که در بقیه صفحات استفاده شده).
const sheetThickness = (value?: number | null, fallback?: string) =>
  value !== undefined && value !== null ? ltrValue(`${(Number(value) / 100).toFixed(2)} ± 0.02`) : fallback || "-";

/**
 * مشخصات فنی را از پاسخ سرور می‌سازد و هر فیلد خالی را از روی کد کالا جبران می‌کند.
 */
export const describePanelSpec = (item: PanelSpecSource): DecodedPanelSpec => {
  const decoded = decodeGoodsCode(item.goods_code);
  const panelType = item.panel_type || decoded?.panelType || "-";
  const thickness =
    item.panel_thickness !== undefined && item.panel_thickness !== null
      ? String(item.panel_thickness)
      : decoded?.thickness || "-";

  return {
    panelType,
    thickness,
    topSheet: item.top_sheet_material || decoded?.topSheet || "-",
    bottomSheet: item.bottom_sheet_material || decoded?.bottomSheet || "-",
    topSheetThickness: sheetThickness(item.top_sheet_thickness, decoded?.topSheetThickness),
    bottomSheetThickness: sheetThickness(item.bottom_sheet_thickness, decoded?.bottomSheetThickness),
    foamDensity:
      item.foam_density !== undefined && item.foam_density !== null
        ? foamDensityWithTolerance(item.foam_density)
        : decoded?.foamDensity || "-",
    foamType: item.foam_type || decoded?.foamType || "-",
    topColor: item.top_sheet_color || decoded?.topColor || "-",
    bottomColor: item.bottom_sheet_color || decoded?.bottomColor || "-",
    description: thickness !== "-" ? `${panelType} ${thickness} سانت` : panelType,
  };
};

// ========== رنگ گروه‌بندی بر اساس شماره فاکتور ==========
// طبق مستند همه ردیف‌های یک سفارش باید یک رنگ داشته باشند و رنگ باید «قطعی» از
// invoice_number ساخته شود؛ پس نگاشت مستقیم روی خود شماره است، نه روی ترتیب ردیف‌ها
// (تا با تغییر صفحه یا فیلتر، رنگ یک سفارش عوض نشود).
export const INVOICE_ROW_COLORS: string[] = [
  "bg-sky-50 dark:bg-sky-500/10",
  "bg-amber-50 dark:bg-amber-500/10",
  "bg-emerald-200 dark:bg-emerald-500/25",
  "bg-violet-50 dark:bg-violet-500/10",
  "bg-rose-50 dark:bg-rose-500/10",
  "bg-cyan-50 dark:bg-cyan-500/10",
  "bg-lime-200 dark:bg-lime-500/25",
  "bg-orange-50 dark:bg-orange-500/10",
];

export const invoiceRowColorClass = (invoiceNumber?: number | null): string => {
  if (invoiceNumber === undefined || invoiceNumber === null) return "";
  const index = Math.abs(Math.trunc(invoiceNumber)) % INVOICE_ROW_COLORS.length;
  return INVOICE_ROW_COLORS[index];
};

/**
 * برچسب «نک‌دار» یک قلم برای نمایش.
 * مقدار نامشخص «-»، true «دارد» و false «ندارد» نشان داده می‌شود.
 * عمداً از ?? استفاده می‌شود نه ||، وگرنه false به «-» تبدیل می‌شود.
 */
export const notchLabel = (value?: boolean | null): string => {
  if (value === undefined || value === null) return "-";
  return value ? "دارد" : "ندارد";
};

/**
 * توضیحات کامل یک کالا برای نمایش کنار کدش در انتخابگر.
 *
 * همه‌ی مشخصه‌های پانل با «-» به هم وصل می‌شوند، مثل:
 *   دیواری ۵ سانت-ورق رو گالوانیزه-ورق پشت گالوانیزه-ضخامت رو ۰.۳۵…
 * مقادیر نامشخص («-») حذف می‌شوند تا رشته پر از خط تیره‌ی بی‌معنا نشود.
 * اگر کد قابل رمزگشایی نباشد، رشته‌ی خالی برمی‌گردد و فراخوان فقط عنوان را
 * نمایش می‌دهد.
 */
export const goodsCodeDescription = (rawCode?: string | null): string => {
  const spec = decodeGoodsCode(rawCode);
  if (!spec) return "";
  const parts: string[] = [
    spec.thickness && spec.thickness !== "-"
      ? `${spec.panelType} ${spec.thickness} سانت`
      : spec.panelType,
    spec.topSheet !== "-" ? `ورق رو ${spec.topSheet}` : "",
    spec.bottomSheet !== "-" ? `ورق پشت ${spec.bottomSheet}` : "",
    spec.topSheetThickness !== "-" ? `ضخامت رو ${spec.topSheetThickness}` : "",
    spec.bottomSheetThickness !== "-" ? `ضخامت پشت ${spec.bottomSheetThickness}` : "",
    spec.foamDensity !== "-" ? `دانسیته ${spec.foamDensity}` : "",
    spec.foamType !== "-" ? `نوع فوم ${spec.foamType}` : "",
    spec.topColor !== "-" ? `رنگ رو ${spec.topColor}` : "",
    spec.bottomColor !== "-" ? `رنگ پشت ${spec.bottomColor}` : "",
  ];
  return parts.filter(Boolean).join("-");
};
